import { useRef, useState } from "react";

import { useT } from "../i18n/LocaleProvider";

// Tour T-29 (owner, 21 October 2026): the browser's own "Choose File" control, styled as
// the rest of the console: a dashed zone that takes a dropped file or a chosen one, and
// says which. The real <input type="file"> is still there (visually hidden, focusable
// and labelled), so a keyboard and a screen reader use the platform's own picker and the
// form's `required` still holds.
export function FileDrop({
  id,
  accept,
  required,
  file,
  onFile,
}: {
  id: string;
  accept: string;
  required?: boolean;
  file: File | null;
  onFile: (file: File | null) => void;
}) {
  const { t } = useT();
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  return (
    <label
      htmlFor={id}
      className={over ? "file-drop file-drop-over" : "file-drop"}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const dropped = e.dataTransfer.files;
        if (dropped.length > 0 && input.current) {
          // Through the real input, so `required` and the form see the file.
          input.current.files = dropped;
          onFile(dropped[0] ?? null);
        }
      }}
    >
      <input
        ref={input}
        id={id}
        className="visually-hidden"
        type="file"
        accept={accept}
        required={required}
        onChange={(e) => onFile(e.target.files?.[0] ?? null)}
      />
      <span className="file-drop-main">{file ? file.name : t("Drop a file here, or choose one")}</span>
      {file ? <span className="file-drop-hint">{t("Choose a different file")}</span> : null}
    </label>
  );
}
